/**
 * Retry policy for Microsoft Graph / SharePoint requests.
 *
 * Split out from sharepoint.service.ts so the policy is unit-testable without a
 * browser, a token, or a network. This module only answers "is this worth
 * another attempt, and how long do I wait?" — the service owns the fetches.
 *
 * Why this exists: a 7-file AI Import submit fires dozens of Graph requests
 * back to back. At that volume a dropped connection is routine, and before this
 * a single one killed the whole batch (2026-07-27).
 */

/**
 * Statuses worth another attempt. 408 is a request timeout, 429 is Graph
 * throttling (it sends Retry-After), and the 5xx set is transient by
 * definition. Everything else — 400/401/403/404/409/413/507 — means the request
 * itself is wrong or the tenant is out of room, and retrying only wastes time.
 */
export const RETRIABLE_STATUSES = [408, 429, 500, 502, 503, 504] as const;

const RETRIABLE_STATUS_SET = new Set<number>(RETRIABLE_STATUSES);

export function isRetriableStatus(status: number): boolean {
  return RETRIABLE_STATUS_SET.has(status);
}

/**
 * A *rejected* fetch (as opposed to a non-ok Response) means the request never
 * completed: DNS, TLS, a dropped connection, a recycled keep-alive socket, or a
 * File whose backing bytes are no longer readable. Every browser surfaces this
 * as a bare TypeError — Safari words it "Load failed", Chrome "Failed to fetch"
 * — with no status and no body. These are the failures most worth retrying.
 *
 * Only call this on an error caught directly around a fetch or a body read;
 * anywhere else a TypeError is far more likely to be a real bug.
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/**
 * Graph sends Retry-After as either delta-seconds or an HTTP-date. Returns null
 * for anything unparseable so the caller falls back to its own backoff.
 * `nowMs` is injected rather than read from the clock to keep this testable.
 */
export function parseRetryAfterMs(
  header: string | null | undefined,
  nowMs: number,
): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (!trimmed) return null;

  // delta-seconds
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed) * 1000;
  }

  // HTTP-date
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at - nowMs);
}

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 8000;

/**
 * Exponential backoff with jitter, capped. `attempt` is 1-based and describes
 * the attempt that just failed, so the first wait is ~500ms.
 *
 * A server-supplied Retry-After always wins (clamped to MAX_DELAY_MS so a
 * throttle response can't wedge the modal for minutes). `random` is injected so
 * tests get a deterministic number.
 */
export function retryDelayMs(
  attempt: number,
  retryAfterMs?: number | null,
  random: number = Math.random(),
): number {
  if (retryAfterMs != null && retryAfterMs >= 0) {
    return Math.min(retryAfterMs, MAX_DELAY_MS);
  }
  const exponential = BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(exponential, MAX_DELAY_MS);
  // Full jitter over [capped/2, capped) so parallel retries don't resynchronize.
  return Math.round(capped / 2 + random * (capped / 2));
}

/** How many times to attempt a single request before giving up. */
export const MAX_ATTEMPTS = 3;

/**
 * Turn a bare "Load failed" into something a user can act on and a developer
 * can locate. `label` names the operation, e.g. `PUT Simple.pdf`.
 */
export function networkErrorMessage(err: unknown, label: string): string {
  const detail = err instanceof Error && err.message ? err.message : String(err);
  return `Network interrupted during ${label} — check your connection and retry (${detail})`;
}

/**
 * User-facing text for a Graph response that came back but wasn't ok. Keeps the
 * status (so throttling and auth expiry are distinguishable at a glance) and
 * trims the body, which for Graph is a long JSON blob.
 */
export function httpErrorMessage(
  label: string,
  status: number,
  body?: string,
): string {
  const hint =
    status === 401 || status === 403
      ? ' — your Microsoft session may have expired, reconnect and retry'
      : status === 429
        ? ' — SharePoint is throttling this account, wait a moment and retry'
        : status === 507
          ? ' — the SharePoint site is out of storage'
          : '';
  const detail = body ? ` ${body.slice(0, 300)}` : '';
  return `${label} failed (${status})${hint}.${detail}`;
}
